import React, {useRef, useState, useCallback, useEffect} from 'react';
import {
    Box,
    Button,
    Typography,
    Popper,
    Grow,
    Paper,
    ClickAwayListener,
    MenuList,
    MenuItem, IconButton
} from '@mui/material';
import {useAuth} from "../security/AuthProvider";
import NotificationsDrawer from "../components/notifications/NotificationsDrawer";
import {IconAppBarNotifications} from "../components/Icons";
import EmployeeAvatar from "../components/EmployeeAvatar";
import {appConfig} from "../config";
import {useMe} from "../hooks/useMe";


interface AppBarProps {
    // Add any additional props you might need
}

const AppBar: React.FC<AppBarProps> = () => {

    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [notificationsDrawerIsOpen, setNotificationsDrawerIsOpen] = useState(false);

    const { logout } = useAuth();
    const {employee, getEmployee} = useMe();

    useEffect(() => {
        getEmployee();
    }, [getEmployee]);

    const handleProfileMenuOpen = useCallback(() => {
        setProfileMenuOpen((prevOpen) => !prevOpen);
    }, []);

    const handleProfileMenuClose = useCallback((event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setProfileMenuOpen(false);
    }, []);

    const handleLogout = useCallback(async () => {
        await logout();
    }, [logout]);

    const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setProfileMenuOpen(false);
        } else if (event.key === 'Escape') {
            setProfileMenuOpen(false);
        }
    }, []);

    const userName = useCallback(() => {
        if (employee.data) {
            return `${employee.data.firstname} ${employee.data.lastname}`;
        }
        return '';
    }, [employee.data]);

    const toggleNotificationsDrawer = useCallback(() => {
        setNotificationsDrawerIsOpen(prevOpen => {
            return !prevOpen;
        });
    }, []);

    const closeNotificationsDrawer = () => {
        setNotificationsDrawerIsOpen(false);
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minHeight: '52px',
                borderBottom: (theme) => theme.palette.customDrawer.border, zIndex: 1 }} >

            <IconButton onClick={toggleNotificationsDrawer} sx={{ fontSize: '1.25rem' }}>
                <IconAppBarNotifications />
            </IconButton>

            <Button
                ref={anchorRef}
                id="composition-button"
                aria-controls={profileMenuOpen ? 'composition-menu' : undefined}
                aria-expanded={profileMenuOpen ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                sx={{ ml: '0.5rem', mr: 3 }}
                startIcon={
                    employee.data
                        ? <EmployeeAvatar employee={employee.data} />
                        : undefined
                }
            >
                {userName()}
            </Button>
            <Popper
                sx={{ zIndex: 999 }}
                open={profileMenuOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleProfileMenuClose}>
                                <MenuList
                                    autoFocusItem={profileMenuOpen}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    <MenuItem disabled={true} onClick={handleProfileMenuClose}>
                                        <Typography variant="navText" sx={{ color: 'inherit' }}>Benutzerkonto</Typography>
                                    </MenuItem>
                                    <MenuItem onClick={handleLogout}>
                                        <Typography variant="navText" sx={{ color: 'inherit' }}>Abmelden</Typography>
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <NotificationsDrawer open={notificationsDrawerIsOpen} onClose={() => closeNotificationsDrawer()} />
        </Box>
    );
};

export default AppBar;